import { unsafeCSS, type CSSResult, type CSSResultGroup } from 'lit';
import * as domtools from '@design.estate/dees-domtools';

const camelToKebab = (name: string) =>
  name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

type TFinalizeStyles = (styles?: CSSResultGroup) => unknown[];

/**
 * Establishes the CSS containment context on `:host` for component-scoped
 * container queries (`cssManager.cssFor*(css, this)`, `this.cssForConstraint()`).
 *
 * The container name and the appended styles are resolved when Lit finalizes
 * the class (at `customElements.define`), not when the decorator runs:
 * TypeScript applies class decorators before the class's own `static styles`
 * initializer, esbuild after it. Resolving at finalize time works under both
 * emits, for either decorator order relative to `@customElement`, and always
 * sees the registered tag in `.is`.
 */
export function containerResponsive() {
  return function (target: any, _context?: unknown) {
    const superFinalizeStyles: TFinalizeStyles = target.finalizeStyles;

    const finalizeStyles: TFinalizeStyles = function (this: any, styles?: CSSResultGroup) {
      // A decorated subclass installs its own override; the most derived one owns the context.
      if (this.finalizeStyles !== finalizeStyles) {
        return superFinalizeStyles.call(this, styles);
      }

      const derivedName = camelToKebab(target.name);
      const tagName: string = target.is || derivedName;

      // `cssManager.cssFor*(css, this)` falls back to the kebab-cased class name when
      // it runs before `.is` exists (esbuild emit). Point those queries at the tag.
      const fixStyle = (style: unknown) => {
        const cssText = (style as CSSResult | undefined)?.cssText;
        if (
          derivedName !== tagName &&
          typeof cssText === 'string' &&
          cssText.includes(`@container ${derivedName}`)
        ) {
          return unsafeCSS(cssText.replaceAll(`@container ${derivedName}`, `@container ${tagName}`));
        }
        return style;
      };

      const ownStyles: unknown[] =
        styles === undefined
          ? []
          : Array.isArray(styles)
            ? (styles as unknown[]).flat(Infinity)
            : [styles];
      return superFinalizeStyles.call(this, [
        ...ownStyles.map(fixStyle),
        domtools.breakpoints.containerContextStyles(tagName),
      ] as CSSResultGroup);
    };

    target.finalizeStyles = finalizeStyles;
    return target;
  };
}
